import { ModalAssistant } from '@mairistem/assistant';
import { Dimmer, Loader, Segment } from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import { isEmpty } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import {
	initialSteps,
	parapheurStep,
	STEP_PARAPHEUR,
	STEP_TELETRANSMISSION,
	STEP_UTILISATEUR,
	teletransmissionStep,
} from '../../../../constants/utilisateurStep';
import type UtilisateurError from '../../../../enums/UtilisateurError';
import type { Bureau } from '../../../../interfaces/bureaux';
import type Collectivite from '../../../../interfaces/collectivite';
import type { Step } from '../../../../interfaces/step';
import type { UtilisateurCollectiviteToCreate } from '../../../../interfaces/utilisateurCollectivite';
import { getBureaux } from '../../../../services/pastellService';
import getCurrentStepIndex from '../../../../utils/getCurrentStepIndex';
import {
	validateStepParapheur,
	validateStepTeletransmission,
	validateStepUser,
} from '../../../../utils/validateSteps';
import UtilisateurSelectedForm from '../../UtilisateurSelectedForm';
import ParapheurStep from './ParapheurStep';
import TeletransmissionStep from './TeletransmissionStep';

interface UtilisateurCreateModalProps {
	onClose: () => void;
	onCreate: (
		utilisateur: UtilisateurCollectiviteToCreate,
		certificat?: File,
	) => void;
	entitesPastell: Collectivite[];
	isLoading?: boolean;
}

const UtilisateurCreateModal = ({
	onClose,
	onCreate,
	entitesPastell,
	isLoading,
}: UtilisateurCreateModalProps) => {
	const [currentStep, setCurrentStep] = React.useState(0);
	const [steps, setSteps] = React.useState<Step[]>([initialSteps]);
	const [newUser, setNewUser] = React.useState<
		Partial<UtilisateurCollectiviteToCreate>
	>({
		collectivites: [],
	});
	const [fieldsOnError, setFieldsOnError] = React.useState<UtilisateurError[]>(
		[],
	);
	const [isAgentTeletransmetteur, setIsAgentTeletransmetteur] =
		React.useState<boolean>(false);
	const [isAgentActe, setIsAgentActe] = React.useState<boolean>(false);
	const [isSignataireParapheur, setIsSignataireParapheur] =
		React.useState<boolean>(false);
	const [certificatFile, setCertificatFile] = React.useState<File | null>(null);

	// États pour stocker les indices d'étapes calculés
	const [parapheurStepIndex, setParapheurStepIndex] =
		React.useState<number>(-1);
	const [teletransmissionStepIndex, setTeletransmissionStepIndex] =
		React.useState<number>(-1);

	// Bureaux
	const [bureaux, setBureaux] = React.useState<{ [key: string]: Bureau[] }>({});
	const [isBureauxLoading, setIsBureauxLoading] = React.useState<boolean>(true);

	const fetchBureaux = async () => {
		try {
			const data = await getBureaux();
			// Regrouper les bureaux par id_e
			const bureauxParEntite: { [key: string]: Bureau[] } = {};

			data.forEach((entite) => {
				const idE = entite.id_e;
				if (!bureauxParEntite[idE]) {
					bureauxParEntite[idE] = [];
				}

				entite.bureaux?.forEach((bureau) => {
					bureauxParEntite[idE].push({
						...bureau,
						isSelected: false,
						id_e: idE,
					});
				});
			});

			setBureaux(bureauxParEntite);
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors de la récupération des bureaux'),
			);
		} finally {
			setIsBureauxLoading(false);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de fetchBureaux
	React.useEffect(() => {
		// Met a jour les indices de chaque étape en fonction des rôles sélectionnés
		const newParapheurStepIndex = getCurrentStepIndex({
			isSignataireParapheur,
			isAgentTeletransmetteur,
			stepType: STEP_PARAPHEUR,
		});
		const newTeletransmissionStepIndex = getCurrentStepIndex({
			isSignataireParapheur,
			isAgentTeletransmetteur,
			stepType: STEP_TELETRANSMISSION,
		});

		setParapheurStepIndex(newParapheurStepIndex);
		setTeletransmissionStepIndex(newTeletransmissionStepIndex);

		// Met à jour la liste des étapes affichées dans le modal
		const updatedSteps: Step[] = [initialSteps];
		if (isSignataireParapheur) {
			updatedSteps.push(parapheurStep);
		}
		if (isAgentTeletransmetteur) {
			updatedSteps.push(teletransmissionStep);

			if (isEmpty(bureaux)) {
				fetchBureaux();
			}
		}
		setSteps(updatedSteps);
		// Correction de l'étape courante si besoin
		setCurrentStep((prevStep) => {
			const maxStep = updatedSteps.length - 1;
			// Si l'étape courante n'existe plus, revenir à la dernière étape disponible
			if (prevStep > maxStep) {
				return maxStep;
			}
			// Si on décoche tout, on revient à 0
			if (!isSignataireParapheur && !isAgentTeletransmetteur) {
				return 0;
			}
			// Sinon, on garde l'étape courante
			return prevStep;
		});
	}, [isSignataireParapheur, isAgentTeletransmetteur, bureaux]);

	const handleChangeCertificatFile = (files: File[]) => {
		setCertificatFile(files[0]);
		setSteps((oldSteps) =>
			oldSteps.map((oldStep, i) => {
				if (teletransmissionStepIndex >= 0 && i !== teletransmissionStepIndex) {
					return oldStep;
				}
				if (teletransmissionStepIndex >= 0 && i === teletransmissionStepIndex) {
					return { ...oldStep, updating: false, validated: false };
				}
				return oldStep;
			}),
		);
	};

	const handleSaveStep = React.useCallback(
		(index: number) => {
			let stepResult = null;
			switch (index) {
				case STEP_UTILISATEUR:
					stepResult = validateStepUser(
						newUser,
						setFieldsOnError,
						isAgentActe,
						isAgentTeletransmetteur,
						isSignataireParapheur,
					);
					break;
				case parapheurStepIndex:
					stepResult = validateStepParapheur(
						newUser.collectivites ?? [],
						setFieldsOnError,
					);
					break;
				case teletransmissionStepIndex:
					stepResult = validateStepTeletransmission(
						certificatFile,
						newUser.hasSlow ?? false,
						setFieldsOnError,
					);
					break;
				default:
					return true;
			}
			stepResult.updating = false;

			setSteps((oldSteps) =>
				oldSteps.map((oldStep, i) => {
					if (i !== index) {
						return oldStep;
					}

					return { ...oldStep, ...stepResult };
				}),
			);

			return stepResult.validated;
		},
		[
			newUser,
			isSignataireParapheur,
			certificatFile,
			parapheurStepIndex,
			teletransmissionStepIndex,
			isAgentTeletransmetteur,
			isAgentActe,
		],
	);

	const handleChangeRoles = (
		_e: React.SyntheticEvent<HTMLElement>,
		{ name, checked }: { name: string; checked: boolean },
	) => {
		switch (name) {
			case 'isAgentTeletransmetteur':
				setIsAgentTeletransmetteur(checked);
				if (checked && !isAgentActe) {
					setIsAgentActe(true);
				}
				break;
			case 'isAgentActe':
				setIsAgentActe(checked);
				break;
			case 'isSignataireParapheur':
				setIsSignataireParapheur(checked);
				break;
			default:
				break;
		}

		setSteps((oldSteps) =>
			oldSteps.map((oldStep, i) => {
				if (i !== STEP_UTILISATEUR) {
					return oldStep;
				}

				return { ...oldStep, updating: false, validated: false };
			}),
		);
	};

	const handleCurrentStepChange = (newStep: number) => {
		handleSaveStep(currentStep);
		setCurrentStep(newStep);
	};

	const handleFinish = React.useCallback(() => {
		const isInfoUserValid = steps[STEP_UTILISATEUR].validated;

		let isTeletransmissionValid = true;
		let isParapheurValid = true;

		if (
			teletransmissionStepIndex >= 0 &&
			teletransmissionStepIndex < steps.length
		) {
			isTeletransmissionValid = !!steps[teletransmissionStepIndex].validated;
		}
		if (parapheurStepIndex >= 0 && parapheurStepIndex < steps.length) {
			isParapheurValid = !!steps[parapheurStepIndex].validated;
		}

		if (!isInfoUserValid) {
			setCurrentStep(STEP_UTILISATEUR);
			return;
		}

		if (!isParapheurValid && parapheurStepIndex >= 0) {
			setCurrentStep(parapheurStepIndex);
			return;
		}

		if (!isTeletransmissionValid && teletransmissionStepIndex >= 0) {
			setCurrentStep(teletransmissionStepIndex);
			return;
		}

		if (isInfoUserValid && isTeletransmissionValid && isParapheurValid) {
			onCreate(
				newUser as UtilisateurCollectiviteToCreate,
				certificatFile ?? undefined,
			);
		}
	}, [
		steps,
		teletransmissionStepIndex,
		parapheurStepIndex,
		onCreate,
		newUser,
		certificatFile,
	]);

	const handleChangeUser = (
		updatedUser: Partial<UtilisateurCollectiviteToCreate>,
	) => {
		setNewUser(updatedUser);

		setSteps((oldSteps) =>
			oldSteps.map((oldStep, i) => {
				if (i !== STEP_UTILISATEUR) {
					return oldStep;
				}

				return { ...oldStep, updating: true, validated: false };
			}),
		);
	};

	return (
		<ModalAssistant
			open
			title="Création d'un utilisateur"
			steps={steps}
			onStepChange={handleCurrentStepChange}
			currentStep={currentStep}
			onClose={onClose}
			onSaveStep={handleSaveStep}
			onFinish={handleFinish}
		>
			<ModalAssistant.Content>
				{isLoading && (
					<Segment basic>
						<Dimmer active inverted>
							<Loader />
						</Dimmer>
					</Segment>
				)}
				{STEP_UTILISATEUR === currentStep && (
					<UtilisateurSelectedForm
						onChange={handleChangeUser}
						errors={fieldsOnError}
						user={{
							...newUser,
							isAgentActe,
							isSignataireParapheur,
							isAgentTeletransmetteur,
						}}
						isCreation
						handleChangeRoles={handleChangeRoles}
					/>
				)}
				{teletransmissionStepIndex === currentStep &&
					isAgentTeletransmetteur && (
						<TeletransmissionStep
							entitesPastell={entitesPastell}
							fieldsOnError={fieldsOnError}
							certificatFile={certificatFile ?? undefined}
							handleChangeCertificatFile={handleChangeCertificatFile}
							newUser={newUser}
							setNewUser={setNewUser}
						/>
					)}
				{parapheurStepIndex === currentStep && isSignataireParapheur && (
					<ParapheurStep
						setBureaux={setBureaux}
						entitesPastell={entitesPastell}
						fieldsOnError={fieldsOnError}
						newUser={newUser}
						setNewUser={setNewUser}
						bureaux={bureaux}
						isBureauxLoading={isBureauxLoading}
					/>
				)}
			</ModalAssistant.Content>
		</ModalAssistant>
	);
};

export default UtilisateurCreateModal;
